import React, { useCallback, useMemo } from 'react';
import Avatar from '@atlaskit/avatar';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import type { IssuesForAssignee } from '@atlassian/jira-business-summary-services/src/services/types.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { Link } from '@atlassian/react-resource-router';
import { useSectionAnalytics } from '../../../common/ui/analytics/index.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import {
	PercentageTableRow,
	SinglePercentageTable,
	type SinglePercentageTableRenderRowArgs,
} from '../../../common/ui/single-percentage-table/index.tsx';
import { useSummaryFeatures } from '../../../controllers/features-context/index.tsx';
import type { PaginatedDataProps } from '../../avp-bridge/with-paginated-data/index.tsx';
import { BoundaryIds, MetricTypes, SectionIds, type ColorScheme } from '../../../common/types.tsx';
import messages from './messages.tsx';

const UserRow = ({
	item: { user },
	totalUnresolvedIssues,
	counterText,
	linksResolver,
	linksTarget,
	count,
	colorScheme,
}: SinglePercentageTableRenderRowArgs<IssuesForAssignee>) => {
	const { formatMessage } = useIntl();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.TeamWorkload,
		BoundaryIds.TeamWorkload,
	);

	const unassignedUser = {
		accountId: undefined,
		avatarUrl: undefined,
		displayName: formatMessage(messages.unassigned),
	};
	const { accountId, avatarUrl, displayName } = user || unassignedUser;

	const onClickHref = linksResolver({
		filter: `assignee = ${accountId ?? 'EMPTY'}`,
		hideDone: true,
	});

	const onRowClick = () => {
		fireSectionAnalytics('viewWithFilter', { count, isUnassigned: !user });
	};

	return (
		<PercentageTableRow
			count={count}
			totalUnresolvedIssues={totalUnresolvedIssues}
			counterText={counterText}
			onClickHref={onClickHref}
			linksTarget={linksTarget}
			onClick={onRowClick}
			linkAriaLabel={displayName}
			colorScheme={colorScheme}
		>
			<Avatar src={avatarUrl} name={displayName} size="small" />
			<Text maxLines={1}>{displayName}</Text>
		</PercentageTableRow>
	);
};

export const TeamWorkload = (
	props: PaginatedDataProps<IssuesForAssignee> & { colorScheme?: ColorScheme },
) => {
	const totalUnresolvedIssues = useMemo(
		() => props.data.reduce((acc, { count }) => acc + count, 0),
		[props.data],
	);

	const tableProps = totalUnresolvedIssues
		? props
		: { ...props, data: [{ id: '', count: 0, user: null }] };

	const { formatMessage } = useIntl();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.TeamWorkload,
		BoundaryIds.TeamWorkload,
	);

	const { linksResolver, linksTarget } = useSummaryFeatures();

	const handleLinkClick = useCallback(() => {
		fireSectionAnalytics('view');
	}, [fireSectionAnalytics]);

	const countSelector = useCallback(({ count }: { count: number }) => count, []);

	return (
		<ChartWithHeaderContainer
			header={
				<>
					<Heading size="small" as="h2">
						<FormattedMessage {...messages.title} />
					</Heading>
					{totalUnresolvedIssues ? (
						<Text>
							<FormattedMessage
								{...messages.description}
								values={{
									link: (chunks) => (
										<Link
											href={linksResolver({
												sortBy: 'assignee',
												hideDone: true,
											})}
											target={linksTarget}
											onClick={handleLinkClick}
										>
											{chunks}
										</Link>
									),
								}}
							/>
						</Text>
					) : (
						<Text>
							<FormattedMessage
								{...messages.emptyDescription}
								values={{
									link: (chunks) => (
										<Link
											href={linksResolver({ openCreateIssueDialog: true })}
											target={linksTarget}
											onClick={handleLinkClick}
										>
											{chunks}
										</Link>
									),
								}}
							/>
						</Text>
					)}
				</>
			}
			chart={
				<SinglePercentageTable
					typeText={formatMessage(messages.typeColumn)}
					distributionText={formatMessage(messages.distributionColumn)}
					counterText={formatMessage(messages.counter)}
					RowComponent={UserRow}
					getKey={({ user }) => user?.accountId ?? 'unassigned'}
					metricType={MetricTypes.DISTRIBUTION}
					countSelector={countSelector}
					{...tableProps}
				/>
			}
		/>
	);
};
